import { AxiosRequestConfig, AxiosResponse } from 'axios';

import {
  productDemoApiRouter,
  isProductDemoWriteWhitelisted,
} from '~modules/product-demo/product-demo-api-router';
import { DemoModeWriteError } from '~modules/product-demo/product-demo-error';
import { getProductDemoRuntimeUserId } from '~modules/product-demo/product-demo-runtime';
import { productDemoStorage } from '~modules/product-demo/product-demo-storage';

import { BaseHttpService } from './base-http-service';

export type HttpRequestConfig = AxiosRequestConfig & {
  _silentAuthFailure?: boolean;
  _skipAuthRefresh?: boolean;
  _allowInProductDemo?: boolean;
};

class HttpServiceImpl extends BaseHttpService {
  private get productDemoUserId() {
    return getProductDemoRuntimeUserId();
  }

  private get isProductDemoEnabled() {
    return productDemoStorage.isEnabled(this.productDemoUserId);
  }

  public get = <T>(
    endpoint: string,
    params?: Record<string, unknown>,
    config?: HttpRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    if (this.isProductDemoEnabled) {
      const demoResponse = productDemoApiRouter.resolve<T>({
        endpoint,
        params,
        userId: this.productDemoUserId,
      });

      if (demoResponse.found) {
        return Promise.resolve(demoResponse.response);
      }
    }

    return this.axiosInstance.get<T>(endpoint, { ...config, params });
  };

  public post = <T, A = AxiosResponse>(
    endpoint: string,
    data: T,
    options?: HttpRequestConfig,
  ): Promise<A> => {
    if (
      this.isProductDemoEnabled &&
      !options?._allowInProductDemo &&
      !isProductDemoWriteWhitelisted(endpoint)
    ) {
      return Promise.reject(new DemoModeWriteError());
    }

    return this.axiosInstance.post<T, A>(endpoint, data, options);
  };

  public put = <T, A = AxiosResponse>(
    endpoint: string,
    data: T,
    options?: HttpRequestConfig,
  ): Promise<A> => {
    if (
      this.isProductDemoEnabled &&
      !options?._allowInProductDemo &&
      !isProductDemoWriteWhitelisted(endpoint)
    ) {
      return Promise.reject(new DemoModeWriteError());
    }

    return this.axiosInstance.put<T, A>(endpoint, data, options);
  };

  public delete = <T>(
    endpoint: string,
    config?: HttpRequestConfig,
  ): Promise<AxiosResponse<T>> => {
    if (
      this.isProductDemoEnabled &&
      !config?._allowInProductDemo &&
      !isProductDemoWriteWhitelisted(endpoint)
    ) {
      return Promise.reject(new DemoModeWriteError());
    }

    return this.axiosInstance.delete<T>(endpoint, config);
  };

  public patch = <T, R = AxiosResponse<T>>(
    endpoint: string,
    data: T,
    options?: HttpRequestConfig,
  ): Promise<R> => {
    if (
      this.isProductDemoEnabled &&
      !options?._allowInProductDemo &&
      !isProductDemoWriteWhitelisted(endpoint)
    ) {
      return Promise.reject(new DemoModeWriteError());
    }

    return this.axiosInstance.patch<T, R>(endpoint, data, options);
  };
}

export const HttpService = new HttpServiceImpl();
